/** @format */

import React, {useContext} from 'react';
import PropTypes from 'prop-types';
import NumberFormat from 'react-number-format';
import {PRODUCT_TYPE_CARTON, SIZE_STATUS_INSTOCK} from 'frontier-constants';
import {Button, Icon} from '@sttark/design-system';
import {QuoteContext} from 'frontier-quoter';
import {If, IfElse} from 'frontier-utilities';

export default function SelectedDie({
    die,
    isNewDie,
    quotedDieCharge,
    productId,
    width,
    length,
    depth,
    onRoundSize,
    isCustomFee,
    diesMatchingShapeOppositeCorners = [],
    onSetCorners,
    corners,
    errors
}) {
    const ctx = useContext(QuoteContext);
    const closestWidth = (Math.round(width * 2) / 2).toFixed(1);
    const closestLength = (Math.round(length * 2) / 2).toFixed(1);
    const closestDepth = (Math.round(depth * 2) / 2).toFixed(1);

    const widthError = errors.find(e => e.category === 'width');
    const lengthError = errors.find(e => e.category === 'length');
    const depthError = errors.find(e => e.category === 'depth');
    const cartonSizeError = errors.find(e => e.category === 'carton_size');
    const labelSizeError = errors.find(e => e.category === 'label_size');

    if (widthError || lengthError || depthError || cartonSizeError || labelSizeError) {
        return (
            <div className="p-4 content-box bg-structure-lighter">
                <h4 className="mt-0">
                    <Icon name={'warning'} color="#df3d33" /> Invalid Size
                </h4>
                <p>
                    {[widthError, lengthError, depthError, cartonSizeError, labelSizeError]
                        .filter(e => e)
                        .map(e => e.message)
                        .join(' ')}
                </p>
            </div>
        );
    } else if (isCustomFee) {
        return (
            <div className="d-md-flex">
                <div
                    className="me-md-3 mt-md-2 mb-5 mb-md-0 d-none"
                    style={{
                        padding: '24px',
                        border: 'solid 1px #000000',
                        textAlign: 'center',
                        flex: '1'
                    }}>
                    <div className="flex-grow-1">
                        <p className="mt-0 font-heading-md">Save ${quotedDieCharge}</p>
                        <p className="font-body-sm">
                            By rounding your size to the nearest half inch, you will save $
                            {quotedDieCharge}. Our Experts can even adjust your artwork for you.
                        </p>
                        <Button
                            small={true}
                            label="Round and Save"
                            onClick={onRoundSize.bind(this, {
                                width: closestWidth,
                                length: closestLength,
                                depth: closestDepth
                            })}
                        />
                    </div>
                </div>
                <div
                    className="d-flex content-box bg-structure-lighter ms-md-3 mt-md-2"
                    style={{padding: '24px', flex: '1'}}>
                    <div className="flex-grow-1">
                        <div
                            className="font-body-xl d-flex"
                            style={{justifyContent: 'space-between', alignItems: 'center'}}>
                            <h5 className="m-0">Custom Size</h5>
                            {quotedDieCharge > 0 && (
                                <NumberFormat
                                    displayType="text"
                                    value={quotedDieCharge}
                                    prefix="+$"
                                />
                            )}
                        </div>
                        {quotedDieCharge > 0 && (
                            <div
                                className="d-flex d-none"
                                style={{
                                    marginBottom: '24px',
                                    justifyContent: 'flex-end',
                                    alignItems: 'center',
                                    fontWeight: 'bold'
                                }}>
                                <p className="font-label-xs">One-Time Fee</p>
                            </div>
                        )}
                        <p className="font-body-sm">
                            A new die will be created for this custom size. Leadtimes start at{' '}
                            {ctx.order.delay_new_size} days for new dies.
                        </p>
                    </div>
                </div>
            </div>
        );
    } else if (
        (isNewDie || (die && die?.status_id !== SIZE_STATUS_INSTOCK)) &&
        productId === PRODUCT_TYPE_CARTON
    ) {
        return (
            <IfElse condition={isNewDie}>
                <div className="p-4 content-box bg-structure-lighter">
                    <h4 className="mt-0">New Size</h4>
                    <p>
                        A new die will be created for this custom size. Leadtimes start at{' '}
                        {ctx.order.delay_new_size} days for new dies.
                    </p>
                </div>
                <div className="p-4 content-box bg-structure-lighter">
                    <h4 className="mt-0">Die #{die?.id} Out of Stock</h4>
                    <p>
                        This die is currently out of stock. Leadtimes start at{' '}
                        {ctx.order.delay_new_size} days for new dies.
                    </p>
                </div>
            </IfElse>
        );
    }
    if (isNewDie && productId !== PRODUCT_TYPE_CARTON) {
        return (
            <div className="d-md-flex">
                {diesMatchingShapeOppositeCorners.length > 0 && (
                    <div
                        className="me-md-3 mt-md-2 mb-5 mb-md-0"
                        style={{
                            padding: '24px',
                            border: 'solid 1px #000000',
                            textAlign: 'center',
                            flex: '1'
                        }}>
                        <div className="flex-grow-1">
                            <p className="mt-0 font-heading-md">Save ${quotedDieCharge}</p>
                            <p className="font-body-sm">
                                By choosing {corners === 1 ? 'Round Corners' : 'Square Corners'} for
                                this size, you will save ${quotedDieCharge}.
                            </p>
                            <Button
                                small={true}
                                label="Update Corners and Save"
                                onClick={onSetCorners.bind(this, corners === 0 ? 1 : 0)}
                            />
                        </div>
                    </div>
                )}
                <div
                    className="d-flex content-box bg-structure-lighter mt-md-2"
                    style={{padding: '24px', flex: '1'}}>
                    <div className="flex-grow-1">
                        <div
                            className="font-body-xl d-flex"
                            style={{justifyContent: 'space-between', alignItems: 'center'}}>
                            <h5 className="m-0">Custom Size</h5>
                            <NumberFormat displayType="text" value={quotedDieCharge} prefix="+$" />
                        </div>
                        <div
                            className="d-flex"
                            style={{
                                marginBottom: '24px',
                                justifyContent: 'flex-end',
                                alignItems: 'center',
                                fontWeight: 'bold'
                            }}>
                            <p className="font-label-xs">One-Time Fee</p>
                        </div>
                        <p className="font-body-sm">
                            A new die will be created for this custom size. Leadtimes start at{' '}
                            {ctx.order.delay_new_size} days for new dies.
                        </p>
                    </div>
                </div>
            </div>
        );
    }

    if (die) {
        return (
            <div className="p-4 content-box bg-structure-lighter">
                <h4 className="mt-0">Die #{die.id}</h4>
                <p>
                    Congrats! You saved some{' '}
                    <IfElse condition={ctx.order.product_type_id === PRODUCT_TYPE_CARTON}>
                        <span>time</span>
                        <span>money</span>
                    </IfElse>{' '}
                    by selecting an existing size.
                </p>
            </div>
        );
    }

    return null;
}

SelectedDie.propTypes = {
    die: PropTypes.shape({
        id: PropTypes.number
    }),
    isNewDie: PropTypes.bool
};
